import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider, Localized } from "@fluent/react";

import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import KButton from "@kikoff/components/src/v1/buttons/KButton";
import KLink from "@kikoff/components/src/v1/navigation/KLink";

import PageAlign from "@component/dashboard/offer/page_align";
import ViewTemplate from "@component/dashboard/offer/view_template";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import Line from "@component/line";
import { GenericSpinner } from "@component/loading_indicators/loading_indicators";
import StreamlinedHeader from "@component/onboarding/streamlined_header";
import { initCreditV2, selectCredit } from "@feature/credit";
import { selectCreditAccount } from "@feature/credit_line";
import {
  selectCreditServiceCheckoutPreview,
  useIsStandaloneSubscriptionEnabled,
} from "@feature/shopping";
import {
  getCreditAccountLowercaseMentalModelName,
  getCreditAccountMentalModelName,
  getStoreLowercaseMentalModelName,
  useCreditServiceGenericMentalModelName,
} from "@src/constants";
import { useOverlaysController } from "@src/overlay";
import { track } from "@util/analytics";
import { l10nResources } from "@util/l10n";
import { TrackImpression } from "@util/track-impression";

import check from "../images/verified.svg";

import ArrowIcon from "./arrow.svg";

import styles from "./styles.module.scss";

const l10n = l10nResources({
  en: `lending-repayment-term-month-subscription = Estimated payback: {$repaymentTermMonths} months
repayment-term-month-subscription = {$repaymentTermMonths} month plan
month-abbr = mo
education-get-started-bullets = 0% APR • No Credit Check • 0 Hidden Fees
education-get-started = Let’s get started
education-title-premium-enabled = Let’s start building your credit.
education-how-it-works = How it Works
education-123 = Building your credit is as easy as 1, 2, 3.
education-step-set-up = Set up
education-step-build-history = Build history
education-step-set-and-forget = Set and forget
education-utilization = Utilization
education-history = History
education-credit-age = Credit Age
education-pay-per-month = You pay \${$monthlyPayment}/mo
education-report-to-bureaus = You make monthly payments. We report your payment history to credit bureaus.
education-watch-credit-grow = Set up autopay and watch your credit grow.
terms-and-conditions = Terms and Conditions
store = store
footnote-store = Kikoff has a number of credit building items available in our
footnote-standalone = Looking to purchase the Credit Service without the Kikoff Credit Account?
footnote-standalone-cta = Sign up here
education-how-it-works-set-up = We set up {$lowercaseCreditAccount} with low utilization for you and report it with a credit limit starting at \${$creditLimit}.
credit-score-proof-title = Your Credit Score
credit-score-proof-disclaimer = Based on Kikoff customers that used the {$creditAccount} product and made consistent on-time payments during account lifetime. This data is based on observed VantageScore 3.0 credit score changes. Payment behavior can have an impact on your credit score, and individual results may vary. Data current as of November 2022.
proof-today = Today
proof-with-kikoff = With Kikoff
proof-main-copy = On average, users who make on-time payments with a starting credit score between {$scoreLowerBound} and {$scoreUpperBound} see an increase of {$creditScoreGain} points during account lifetime
money-back-guarantee-title = Our satisfaction guarantee
money-back-guarantee-description = Your financial future and your satisfaction are top priorities for us. Otherwise, cancel within 45 days and you'll get your money back.
`,
  es: `repayment-term-month-subscription = Suscripción de {$repaymentTermMonths} meses
repayment-term-month-subscription = Plan de {$repaymentTermMonths} meses
month-abbr = mes
education-get-started-bullets = 0% APR • Sin verificación de crédito • 0 cargos ocultos
education-get-started = Empecemos
education-title-premium-enabled = Comencemos a construir su crédito.
education-how-it-works = Cómo funciona
education-123 = Construir su puntaje de crédito es tan fácil como 1, 2, 3.
education-step-set-up = Configurar
education-step-build-history = Historial de compilación
education-step-set-and-forget = Establecer y olvidar
education-utilization = Utilización
education-history = Historia
education-credit-age = Edad del crédito
education-pay-per-month = Pagas \${$monthlyPayment}/mes
education-report-to-bureaus = Haces pagos mensuales. Reportamos tu historial de pagos a las agencias de crédito.
education-watch-credit-grow = Configure el pago automático y vea crecer su crédito.
terms-and-conditions = Términos y condiciones
store = tienda
footnote-store = Kikoff tiene varios artículos de creación de crédito disponibles en nuestra
footnote-standalone = ¿Estás buscando comprar el Servicio de Crédito sin la Cuenta de Crédito Kikoff?
footnote-standalone-cta = Regístrate aquí
education-how-it-works-set-up = Configuramos una {$lowercaseCreditAccount} con baja utilización para usted y la informamos con una línea de crédito que comienza en \${$creditLimit}.
credit-score-proof-title = Su Puntaje de Crédito
credit-score-proof-disclaimer = Basado en clientes de Kikoff que usaron el producto {$creditAccount} y realizaron pagos puntuales constantes durante la vigencia de la cuenta. Estos datos se basan en los cambios observados en la puntuación de crédito de VantageScore 3.0. El comportamiento de pago puede tener un impacto en su puntaje de crédito y los resultados individuales pueden variar. Datos actualizados a noviembre de 2022.
proof-today = Hoy
proof-with-kikoff = Con Kikoff
proof-main-copy = En promedio, los usuarios que realizan pagos a tiempo con un puntaje de crédito inicial entre {$scoreLowerBound} y {$scoreUpperBound} ven un aumento de {$creditScoreGain} puntos durante la vida útil de la cuenta
money-back-guarantee-title = Nuestra garantía de satisfacción
money-back-guarantee-description = Su futuro financiero y su satisfacción son nuestra máxima prioridad. De lo contrario, cancela dentro de los 45 días y recuperarás tu dinero.
`,
});

function StreamlinedGetStarted({ page }) {
  const [preview] = useSelector(
    selectCreditServiceCheckoutPreview.load("BASIC")
  );

  return (
    <div className={styles["get-started-section"]}>
      <LocalizationProvider l10n={l10n}>
        <KButton
          data-cy="get-started-button"
          variant="black"
          size="full-width"
          className={styles["get-started-button"]}
          state={preview ? "active" : "disabled"}
          disabled={!preview}
          onClick={() => {
            track.tap("CA - Activation - Get Started Button");

            page.controller.next();
          }}
        >
          <Localized id="education-get-started" />
        </KButton>
        <div className={styles["bullet-points"]}>
          <Localized id="education-get-started-bullets" />
        </div>
      </LocalizationProvider>
    </div>
  );
}

function StreamlinedProductEntry() {
  const emphasizeLending = useSelector((state) => state.page.emphasizeLending);

  const preview = useSelector((state) => state.shopping.checkoutPreview);

  const productTitle = useCreditServiceGenericMentalModelName(true);

  if (!preview) {
    return <GenericSpinner center />;
  }

  const repaymentTermKey = emphasizeLending
    ? "lending-repayment-term-month-subscription"
    : "repayment-term-month-subscription";

  const { product } = preview.order.orderItems[0];
  let repaymentTerm = "";

  repaymentTerm = l10n.getString(repaymentTermKey, {
    repaymentTermMonths: product.repaymentTerm,
  });

  return (
    <ContainerButton className={styles["header-type-product-entry"]}>
      <LocalizationProvider l10n={l10n}>
        <div className={styles["product-titles"]}>
          <div
            className={styles["product-title"]}
            data-cy="product-entry-title"
          >
            {productTitle}
          </div>
          <div className={styles["product-subtitle"]}>
            <div className={styles["repayment-term"]}>{repaymentTerm}</div>
          </div>
        </div>
      </LocalizationProvider>
    </ContainerButton>
  );
}

function StreamlinedHowItWorks() {
  const lowercaseCreditAccount = getCreditAccountLowercaseMentalModelName();

  const creditLimit = 750;

  return (
    <div className={styles["how-it-works-section"]}>
      <LocalizationProvider l10n={l10n}>
        <div className={styles["how-it-works-title"]}>
          <Localized id="education-how-it-works" />
        </div>
        <div className={styles["easy-as-123"]}>
          <Localized id="education-123" />
        </div>
        <div className={styles["easy-steps"]}>
          <div className={styles["easy-step"]}>
            <div className={styles["easy-step-number"]}>1</div>
            <div className={styles["easy-step-content"]}>
              <div className={styles["easy-step-title"]}>
                <Localized id="education-step-set-up" />
              </div>
              <p>
                <Localized
                  id="education-how-it-works-set-up"
                  vars={{ creditLimit, lowercaseCreditAccount }}
                />
              </p>
            </div>
            <div className={styles["easy-step-tag"]}>
              <img src={ArrowIcon.src} className={styles["arrow-down"]} />{" "}
              <Localized id="education-utilization" />
            </div>
          </div>
          <div className={styles["easy-step"]}>
            <div className={styles["easy-step-number"]}>2</div>
            <div className={styles["easy-step-content"]}>
              <div className={styles["easy-step-title"]}>
                <Localized id="education-step-build-history" />
              </div>
              <p>
                <Localized id="education-report-to-bureaus" />
              </p>
            </div>
            <div className={styles["easy-step-tag"]}>
              <img src={ArrowIcon.src} className={styles["arrow-up"]} />{" "}
              <Localized id="education-history" />
            </div>
          </div>
          <div className={styles["easy-step"]}>
            <div className={styles["easy-step-number"]}>3</div>
            <div className={styles["easy-step-content"]}>
              <div className={styles["easy-step-title"]}>
                <Localized id="education-step-set-and-forget" />
              </div>
              <p>
                <Localized id="education-watch-credit-grow" />
              </p>
            </div>
            <div className={styles["easy-step-tag"]}>
              <img src={ArrowIcon.src} className={styles["arrow-up"]} />{" "}
              <Localized id="education-credit-age" />
            </div>
          </div>
        </div>
      </LocalizationProvider>
    </div>
  );
}

function StoreLink() {
  return (
    <KLink
      variant="primary"
      data-cy="store-catalog"
      href="./catalog"
      onClick={() => {
        track.tap("CA - Activation - Select Another Item");
      }}
    >
      {getStoreLowercaseMentalModelName()}
    </KLink>
  );
}

function TcLink() {
  return (
    <KLink variant="primary" href="https://kikoff.com/terms.pdf" type="pdf">
      <Localized id="terms-and-conditions" />
    </KLink>
  );
}

const CreditScoreIncreaseProofBuckets = {
  "300s": {
    scoreLowerBound: 300,
    scoreUpperBound: 400,
    creditScoreGain: 159,
    barUpperBound: 675,
    barLowerBound: 300,
  },
  "400s": {
    scoreLowerBound: 400,
    scoreUpperBound: 500,
    creditScoreGain: 101,
    barUpperBound: 675,
    barLowerBound: 300,
  },
  "500s": {
    scoreLowerBound: 500,
    scoreUpperBound: 600,
    creditScoreGain: 47,
    barUpperBound: 675,
    barLowerBound: 475,
  },
};

interface CreditScoreIncreaseProofProps {
  currentScore: number;
}

function CreditScoreIncreaseProof({
  currentScore,
}: CreditScoreIncreaseProofProps) {
  let bucket;
  if (currentScore < 400) {
    bucket = CreditScoreIncreaseProofBuckets["300s"];
  } else if (currentScore < 500) {
    bucket = CreditScoreIncreaseProofBuckets["400s"];
  } else {
    bucket = CreditScoreIncreaseProofBuckets["500s"];
  }

  return (
    <>
      <Line className={styles["line-with-space"]} />
      <div className={styles["proof-wrapper"]}>
        <div className="text:large+">
          {l10n.getString("proof-main-copy", {
            creditScoreGain: bucket.creditScoreGain,
            scoreUpperBound: bucket.scoreUpperBound,
            scoreLowerBound: bucket.scoreLowerBound,
          })}
          *.
        </div>
        <div className={styles["credit-score-progress"]}>
          <div className={styles["tag-container"]}>
            <div
              className={styles["today-tag"]}
              style={
                {
                  "--position":
                    (currentScore - bucket.barLowerBound) /
                    (bucket.barUpperBound - bucket.barLowerBound),
                } as React.CSSProperties
              }
            >
              <div className="text:micro color:moderate">
                {l10n.getString("proof-today")}
              </div>
              <div className="text:small+ color:moderate">{currentScore}</div>
              <div className="color:moderate">  </div>
            </div>
            <div
              className={styles["kikoff-tag"]}
              style={
                {
                  "--position":
                    (currentScore -
                      bucket.barLowerBound +
                      bucket.creditScoreGain) /
                    (bucket.barUpperBound - bucket.barLowerBound),
                } as React.CSSProperties
              }
            >
              <div className="text:micro color:black-800">
                {l10n.getString("proof-with-kikoff")}
              </div>
              <div className="text:large++ color:black-800">
                {currentScore + bucket.creditScoreGain}
              </div>
              <div className="color:black-800">  </div>
            </div>
          </div>
          <div
            className={styles["bar"]}
            style={
              {
                "--before-kikoff": currentScore - bucket.barLowerBound,
                "--after-kikoff": bucket.creditScoreGain,
                "--total-length": bucket.barUpperBound - bucket.barLowerBound,
              } as React.CSSProperties
            }
          />
        </div>
      </div>
    </>
  );
}

function MoneyBackGuaranteeExplained() {
  const overlays = useOverlaysController();

  return (
    <ContainerButton
      onClick={() => {
        overlays.push("credit_line/money_back_guarantee");
      }}
    >
      <div className={`mt-3 ${styles["money-back-guarantee-section"]}`}>
        <img src={check.src} />
        <div className="mt-1 text:regular+">
          {l10n.getString("money-back-guarantee-title")}
        </div>
        <div className={`mt-1 text:regular ${styles["description"]}`}>
          {l10n.getString("money-back-guarantee-description")}
        </div>
      </div>
    </ContainerButton>
  );
}

interface StreamlinedEducationViewProps extends PageRegistration {
  showBack?: boolean;
  showMoneyBackGuaranteeExplained?: boolean;
}

function StreamlinedEducationView({
  configFlow,
  page,
  showBack = false,
}: StreamlinedEducationViewProps) {
  const dispatch = useDispatch();

  const overlays = useOverlaysController();

  const userAuthenticated = useSelector((state) => state.user.authenticated);
  const creditLine = useSelector(selectCreditAccount());

  const isStandaloneSubscriptionEnabled = useIsStandaloneSubscriptionEnabled();

  const reactivating = creditLine?.closedAt;

  const credit =
    process.env.NODE_ENV === "production"
      ? useSelector(selectCredit())
      : { currentCreditScore: { score: 350 } };
  const displayCreditScoreProof =
    credit?.currentCreditScore?.score > 300 &&
    credit?.currentCreditScore?.score < 600;

  configFlow.none();

  useEffect(() => {
    if (!credit && userAuthenticated) dispatch(initCreditV2());
    if (reactivating) track.impression("CA - Activation - Restart Screen");
    track.impression("CA - Activation - Screen");
  }, []);

  const creditAccount = getCreditAccountMentalModelName();

  return (
    <PageAlign
      className={styles["page-align-no-padding"]}
      scroll
      overscroll={false}
    >
      <LocalizationProvider l10n={l10n}>
        <ViewTemplate className={styles["flat-payment-view"]}>
          <StreamlinedHeader
            displayBackButton={showBack}
            displayCloseButton
            page={page}
          />
          <h3
            className={styles["streamlined-education-title"]}
            data-cy="streamlined-education-title"
          >
            <Localized id="education-title-premium-enabled" />
          </h3>
          <div>
            <div className={styles["flat-card"]}>
              <StreamlinedProductEntry />
              <StreamlinedGetStarted page={page} />
            </div>

            {displayCreditScoreProof && (
              <CreditScoreIncreaseProof
                currentScore={credit.currentCreditScore.score}
              />
            )}

            <StreamlinedHowItWorks />
          </div>
          <Line />
          <div className={styles["select-another-item-wrapper"]}>
            <MoneyBackGuaranteeExplained />

            <div className={`${styles["footnotes"]} text:small color:moderate`}>
              <div>
                <Localized id="footnote-store" /> <StoreLink />. <TcLink />
              </div>
              {isStandaloneSubscriptionEnabled && (
                <TrackImpression
                  eventName="Standalone Subscription - Entrypoint"
                  wrap
                >
                  <Localized id="footnote-standalone" />{" "}
                  <ContainerButton
                    inline
                    onClick={() => {
                      track.tap("Standalone Subscription - Entrypoint");
                      overlays.push(
                        "src/pages/dashboard/store/_views/streamlined_education/standalone"
                      );
                    }}
                  >
                    <Localized id="footnote-standalone-cta" />
                  </ContainerButton>
                </TrackImpression>
              )}
              {displayCreditScoreProof && (
                <div>
                  *
                  <Localized
                    id="credit-score-proof-disclaimer"
                    vars={{ creditAccount }}
                  />
                </div>
              )}
            </div>
          </div>
        </ViewTemplate>
      </LocalizationProvider>
    </PageAlign>
  );
}

export default StreamlinedEducationView;
